import React, { useContext, useEffect, useState, useMemo, useRef } from 'react';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Alert from '@amzn/meridian/alert';
import refreshTokens from '@amzn/meridian-tokens/base/icon/refresh';
import Icon from '@amzn/meridian/icon';

import {
  AddCoachingCommentResponse,
  CoachingCommentsData,
  CommentThread,
  CommentThreadType,
} from '../../api/CoachingComments/schema';
import { FormattedMessage, useIntl } from 'react-intl';
import { StateContext } from '../../GlobalStateStore';
import DataSource from '../../api';
import Button from '@amzn/meridian/button';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import { getLocaleStringFormatted } from '../../translations';
import DataState from '../../Types/DataState';
import TimeoutToast from '../CoachingForm/AutoSave/TimeoutToast';
import Textarea from '@amzn/meridian/textarea';
import Loader from '@amzn/meridian/loader';
import moment from 'moment';
import {
  canAddComment,
  canViewThread,
  DEFAULT_COACHING_COMMENT_DATA,
  determineUserRole,
  getFullName,
  getNotificationReceiver,
  getCommentPlaceholderMessage,
  getEmptyThreadMessage,
  getThreadTabLabel,
  getSuccessToastMessage,
} from './Helper';
import { getRequesterRole } from '../OMCoaching/helper';
import { processCommentThreads } from './ProcessThreads';
import { Profile } from '../../Types';
import { canAccessAgentResourceErrKeyword } from '../ContactListening/utils/ContactListeningConstants';
import CoachingEmptyState from '../../Assets/coachingEmptyState.png';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import { isFeatureEnabled, Feature } from '../../config/FeaturesConfig';
import { getCurrentWeek } from '../../Util';

interface CommentThreadProps {
  thread: CommentThread;
  profile: Profile;
  locale: string;
  managerProfile: Profile | null;
}

const CoachingCommentThread: React.FC<CommentThreadProps> = ({
  thread,
  profile,
  locale,
  managerProfile,
}) => {
  return (
    <Column width='100%' alignmentHorizontal='start' spacing='200'>
      {thread.comments.map((comment, index) => {
        let timestampStr = '';
        const safeTimestamp = Date.parse(comment.timestamp);
        if (!isNaN(safeTimestamp)) {
          timestampStr = moment(safeTimestamp)
            .locale(locale)
            .format('MMM D, YYYY h:mm A');
        }

        return (
          <React.Fragment key={comment.commentID}>
            <Row width='100%' alignmentVertical='top' spacing='200'>
              <div style={{ overflow: 'hidden', flexShrink: 0 }}>
                <img
                  src={`https://internal-cdn.amazon.com/badgephotos.amazon.com/?uid=${comment.authorAlias}`}
                  style={{
                    height: '40px',
                    width: '40px',
                    objectFit: 'cover',
                    borderRadius: '50%',
                  }}
                  alt={comment.authorAlias}
                />
              </div>
              <Column width='100%' spacing='100'>
                <Box
                  type={'fill'}
                  spacingInset='400'
                  width={'100%'}
                  height={'100%'}
                  minHeight={'100%'}
                >
                  <Text type='b300' data-cy={`comment-${index}`}>
                    <b>
                      {getFullName(
                        comment.authorAlias,
                        profile,
                        managerProfile
                      )}
                    </b>{' '}
                    : {comment.comment}{' '}
                  </Text>
                </Box>
                <Row alignmentHorizontal='end'>
                  <Text type='b200' color='secondary' alignment='end'>
                    {timestampStr}
                  </Text>
                </Row>
              </Column>
            </Row>
          </React.Fragment>
        );
      })}
    </Column>
  );
};

export interface CoachingCommentModalProps {
  isOpen: boolean;
  setIsOpen: any;
  noteID: string;
  coachingID: string;
  coach: string;
  isCoachingIdEmpty: boolean;
  closedCoaching: boolean;
  profile: Profile;
  page: string;
}

const getThreadId = (thread?: CommentThread): string => {
  if (!thread) return '';

  const sortedParticipants = thread.commentThreadParticipants.sort();

  return `${thread.commentThreadType}_${sortedParticipants.join('_')}`;
};

export const CoachingCommentModal: React.FC<CoachingCommentModalProps> = ({
  isOpen,
  setIsOpen,
  noteID,
  coachingID,
  coach,
  isCoachingIdEmpty,
  closedCoaching,
  profile,
  page,
}) => {
  const intl = useIntl();
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const {
    requesterUsername,
    isTm,
    isOm,
    requesterOwner,
    userID,
    cscEntityPrefix,
  } = inspireOptions.data;
  const [getCoachingCommentState, setGetCoachingCommentState] = useState(
    DataState.INITIALIZED
  );
  const [addCoachingComment, setAddCoachingComment] =
    useState<AddCoachingCommentResponse>();
  const [addCoachingCommentState, setAddCoachingCommentState] = useState(
    DataState.INITIALIZED
  );
  const [coachingCommentData, setCoachingCommentData] =
    useState<CoachingCommentsData>(DEFAULT_COACHING_COMMENT_DATA);
  const [lastRefreshTimestamp, setLastRefreshTimestamp] = useState(
    Date().toString()
  );
  const [addCommentValue, setAddCommentValue] = useState('');
  const [selectedTab, setSelectedTab] = useState<string | null>(null);

  const [managerProfile, setManagerProfile] = useState<Profile | null>(null);
  const [managerProfileState, setManagerProfileState] = useState(
    DataState.INITIALIZED
  );

  const enableOMComments = isFeatureEnabled(Feature.OM_COACHING_COMMENTS);

  const getDefaultTabId = (threads: CommentThread[]): string | null => {
    if (threads.length === 0) return null;

    const csaTmThreadWithComments = threads.find(
      thread =>
        thread.commentThreadType === CommentThreadType.CSA_TM &&
        thread.comments &&
        thread.comments.length > 0
    );

    if (csaTmThreadWithComments) {
      return getThreadId(csaTmThreadWithComments);
    }

    const anyThreadWithComments = threads.find(
      thread => thread.comments && thread.comments.length > 0
    );

    const defaultThread = anyThreadWithComments || threads[0];
    return defaultThread ? getThreadId(defaultThread) : null;
  };

  async function loadComments(): Promise<void> {
    setGetCoachingCommentState(DataState.LOADING);
    const role = determineUserRole(isOm, isTm);
    const queryVariables = apiNaws.prepareQueryForGetCoachingComments(
      noteID,
      requesterUsername,
      role
    );
    await apiNaws.getCoachingComments(
      queryVariables,
      setCoachingCommentData,
      setGetCoachingCommentState
    );

    setLastRefreshTimestamp(new Date().toString());
  }

  const coachingCommentDataWithThreads: CoachingCommentsData = useMemo(() => {
    if (
      managerProfileState === DataState.LOADED &&
      getCoachingCommentState === DataState.LOADED
    ) {
      const updatedThreads = processCommentThreads(
        coachingCommentData.commentThreads,
        isOm,
        isTm,
        requesterUsername,
        profile,
        managerProfile
      );

      if (updatedThreads && updatedThreads.length > 0) {
        updatedThreads.forEach(thread => {
          if (thread.comments && thread.comments.length > 0) {
            thread.comments.sort((a, b) => {
              let dateA = new Date(a.timestamp).getTime();
              let dateB = new Date(b.timestamp).getTime();
              return dateA - dateB;
            });
          }
        });
      }
      return {
        ...coachingCommentData,
        commentThreads: updatedThreads,
      };
    }
    return DEFAULT_COACHING_COMMENT_DATA;
  }, [managerProfileState, getCoachingCommentState]);

  const filteredThreads = coachingCommentDataWithThreads.commentThreads.filter(
    thread =>
      canViewThread(thread.commentThreadType, isOm, isTm, enableOMComments) &&
      thread.commentThreadParticipants.length > 0
  );

  const currentTab = selectedTab || getDefaultTabId(filteredThreads);

  const currentThread = coachingCommentDataWithThreads.commentThreads.find(
    thread => getThreadId(thread) === currentTab
  );
  const currentThreadType =
    currentThread?.commentThreadType || CommentThreadType.CSA_TM;
  const canUserAddComment = canAddComment(
    currentThreadType,
    isOm,
    isTm,
    enableOMComments
  );

  const locale = getLocaleStringFormatted();
  const apiNaws = DataSource.getDataFeed('NAWS');
  const messageEndRef = useRef<HTMLDivElement>(null);

  // Automatically scrolls to the bottom of the chat window
  const scrollToLatestMessage = (behavior: ScrollBehavior = 'auto') => {
    messageEndRef?.current?.scrollIntoView({
      behavior,
    });
  };

  const determineManagerProfileToLoad = (): string | null => {
    if (isOm && profile.managers && profile.managers.length > 0) {
      return profile.managers[0].userID;
    } else if (isTm) {
      return userID;
    }
    return null;
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedTab(null);
      loadComments();

      const userIdToLoad = determineManagerProfileToLoad();

      if (userIdToLoad) {
        loadManagerProfile(userIdToLoad);
      } else {
        setManagerProfile(null);
        setManagerProfileState(DataState.LOADED);
      }

      setAddCommentValue('');
    }
  }, [isOpen]);

  async function loadManagerProfile(userId: string): Promise<void> {
    setManagerProfileState(DataState.LOADING);

    const queryVariables = apiNaws.prepareQuery(
      getCurrentWeek(),
      userId,
      cscEntityPrefix
    );

    await apiNaws.getProfileData(
      queryVariables,
      setManagerProfile,
      setManagerProfileState
    );
  }

  useEffect(() => {
    if (addCoachingCommentState === DataState.LOADED) {
      loadComments();
      setAddCommentValue('');
      setAddCoachingCommentState(DataState.INITIALIZED);
    }
  }, [addCoachingCommentState]);

  useEffect(() => {
    if (
      isOpen &&
      getCoachingCommentState === DataState.LOADED &&
      currentThread &&
      currentThread.comments &&
      currentThread.comments.length > 0
    ) {
      setTimeout(() => scrollToLatestMessage(), 1);
    }
  }, [isOpen, getCoachingCommentState, currentThread, currentTab]);

  const onClickAddCommentSendButton = async () => {
    if (!currentThread) return;

    let notificationReceivers;

    if (!isTm && !isOm) {
      const csaManagerAlias = profile.managers[0]?.alias;
      notificationReceivers = csaManagerAlias ? [csaManagerAlias] : [];
      if (coach && coach !== csaManagerAlias) {
        notificationReceivers.push(coach);
      }
    } else if (isTm && currentThreadType === CommentThreadType.CSA_TM) {
      notificationReceivers = [profile.user.alias];
    } else {
      notificationReceivers = getNotificationReceiver(
        currentThread,
        requesterUsername
      );
    }

    const queryVariables = apiNaws.prepareQueryForAddCoachingComment(
      noteID,
      isCoachingIdEmpty ? '' : coachingID,
      requesterUsername,
      addCommentValue,
      notificationReceivers,
      requesterOwner,
      currentThreadType,
      notificationReceivers.join(',')
    );

    await apiNaws.addCoachingComment(
      queryVariables,
      setAddCoachingComment,
      setAddCoachingCommentState
    );
  };
  return (
    <>
      <Modal
        title={intl.formatMessage(
          {
            id: 'cs_perfportal_coaching_comment_modal_heading',
            defaultMessage: `Add comment to coaching session : ${coachingID}`,
          },
          { coachingId: coachingID }
        )}
        open={isOpen}
        onClose={setIsOpen(false)}
        scrollContainer='viewport'
        closeLabel='Close'
        aria-describedby='modal-description'
        width={'900px'}
        bodySpacingInset='450'
      >
        {getCoachingCommentState === DataState.LOADING ||
        managerProfileState === DataState.LOADING ? (
          <Box height='fill' width='fill' spacingInset='300'>
            <Row
              width='100%'
              widths='fill'
              alignmentHorizontal='center'
              spacingInset='500'
            >
              <Box minHeight={70}>
                <div style={{ textAlign: 'center' }}>
                  <Loader type='circular' size='large' />
                </div>
              </Box>
            </Row>
          </Box>
        ) : getCoachingCommentState === DataState.ERROR ? (
          <Box height='fill' width='fill' spacingInset='300'>
            <Alert type='error'>
              <FormattedMessage
                id='cs_perfportal_error_refresh_page'
                defaultMessage='Something went wrong. Please try refreshing the page. 
                If the problem persists, please submit a SIM ticket for us to troubleshoot.'
              />
            </Alert>
          </Box>
        ) : (
          <Box height='fill' width='fill' spacingInset='300'>
            <Row alignmentHorizontal='end' spacing='100'>
              <Text alignment='end' type='b200'>
                {intl.formatMessage({
                  id: 'cs_perfportal_coaching_comment_modal_last_updated_at',
                  defaultMessage: 'Last updated at',
                }) +
                  ' ' +
                  moment(lastRefreshTimestamp)
                    .locale(locale)
                    .format('MMM D, YYYY h:mm A')}
              </Text>
              <Button
                data-csa-c-type='button'
                data-csa-c-slot-id='coaching-comment-refresh-button-slot'
                data-csa-c-content-id={`coaching-comment-refresh-button-for-${coachingID}`}
                data-csa-c-noteid={noteID}
                data-csa-c-tag1={page}
                data-csa-c-tag2='Coaching History'
                data-csa-c-tag3='Case Management'
                data-csa-c-coachingID={coachingID}
                data-csa-c-is-tm={isTm}
                data-csa-c-requester-role={getRequesterRole(isOm, isTm)}
                onClick={loadComments}
                type='link'
                size='small'
              >
                <Icon tokens={refreshTokens} />{' '}
                {intl.formatMessage({
                  id: 'cs_perfportal_coaching_comment_modal_refresh',
                  defaultMessage: 'Refresh',
                })}
              </Button>
            </Row>

            {closedCoaching && filteredThreads.length === 0 ? (
              <Column alignmentHorizontal='center' alignmentVertical='center'>
                <div>
                  <img src={CoachingEmptyState} alt='no contacts' />
                </div>
                <Text>
                  <FormattedMessage
                    id='cs_perfportal_coaching_comment_modal_empty'
                    defaultMessage='There are no comments to display for this coaching session'
                  />
                </Text>
              </Column>
            ) : filteredThreads.length > 0 ? (
              <Column width='100%' alignmentHorizontal='start' spacing='200'>
                <TabGroup
                  value={currentTab}
                  onChange={setSelectedTab}
                  aria-labelledby='tablist-heading'
                >
                  {filteredThreads.map(thread => {
                    const threadId = getThreadId(thread);
                    const otherParticipant =
                      thread.commentThreadParticipants.find(
                        participant => participant !== requesterUsername
                      );

                    return (
                      <Tab
                        key={threadId}
                        value={threadId}
                        aria-controls={`tabpanel-${threadId}`}
                        id={`tab-${threadId}`}
                      >
                        {getThreadTabLabel(thread, isOm, otherParticipant)}
                      </Tab>
                    );
                  })}
                </TabGroup>
                <Box width='100%' height='400px' overflowY='auto'>
                  {filteredThreads.map(thread => {
                    const threadId = getThreadId(thread);
                    return (
                      currentTab === threadId && (
                        <Box
                          key={threadId}
                          role='tabpanel'
                          id={`tabpanel-${threadId}`}
                          aria-labelledby={`tab-${threadId}`}
                          width='100%'
                          spacingInset='300'
                        >
                          {thread.comments.length > 0 ? (
                            <>
                              <CoachingCommentThread
                                thread={thread}
                                profile={profile}
                                locale={locale}
                                managerProfile={managerProfile}
                              />
                              {/* Empty div used as a scroll target */}
                              <div aria-hidden='true' ref={messageEndRef} />
                            </>
                          ) : (
                            <Column
                              alignmentHorizontal='center'
                              alignmentVertical='center'
                              spacing='200'
                            >
                              <Text>
                                {getEmptyThreadMessage(
                                  thread.commentThreadType,
                                  isOm,
                                  isTm
                                )}
                              </Text>
                            </Column>
                          )}
                        </Box>
                      )
                    );
                  })}
                </Box>
              </Column>
            ) : (
              <Column alignmentHorizontal='center' alignmentVertical='center'>
                <Text>
                  <FormattedMessage
                    id='cs_perfportal_coaching_comment_modal_empty'
                    defaultMessage='There are no comments to display for this coaching session'
                  />
                </Text>
              </Column>
            )}

            {!closedCoaching && (
              <>
                <Box width='fill' spacingInset='400'>
                  <Textarea
                    value={addCommentValue}
                    data-cy='comment-input'
                    onChange={setAddCommentValue}
                    disabled={!canUserAddComment}
                    error={addCommentValue.length > 500}
                    errorMessage={
                      addCommentValue.length > 500
                        ? intl.formatMessage({
                            id: 'cs_perfportal_coaching_comment_comment_size_error',
                            defaultMessage:
                              'Comment must be under 500 characters.',
                          })
                        : ''
                    }
                    placeholder={getCommentPlaceholderMessage(
                      isOm,
                      isTm,
                      currentThreadType,
                      intl
                    )}
                  />
                </Box>

                {!canUserAddComment && (
                  <Text type='b200' color='secondary'>
                    <FormattedMessage
                      id='cs_perfportal_coaching_comment_no_permission'
                      defaultMessage='Only the associate and team manager can comment on this thread.'
                    />
                  </Text>
                )}

                <ModalFooter>
                  <Row alignmentHorizontal='end'>
                    <Button
                      data-csa-c-type='button'
                      data-csa-c-slot-id='coaching-comment-close-button-slot'
                      data-csa-c-content-id={`coaching-comment-close-button-for-${coachingID}`}
                      data-csa-c-noteid={noteID}
                      data-csa-c-tag1={page}
                      data-csa-c-tag2='Coaching History'
                      data-csa-c-tag3='Case Management'
                      data-csa-c-coachingID={coachingID}
                      data-csa-c-is-tm={isTm}
                      data-csa-c-requester-role={getRequesterRole(isOm, isTm)}
                      type='secondary'
                      size='medium'
                      minWidth={150}
                      onClick={setIsOpen(false)}
                    >
                      <FormattedMessage
                        id='cs_perfportal_cancel'
                        defaultMessage='Cancel'
                      />
                    </Button>

                    <Button
                      data-csa-c-type='button'
                      data-csa-c-slot-id='coaching-comment-send-button-slot'
                      data-csa-c-content-id={`coaching-comment-send-button-for-${coachingID}`}
                      data-csa-c-noteid={noteID}
                      data-csa-c-tag1={page}
                      data-csa-c-tag2='Coaching History'
                      data-csa-c-tag3='Case Management'
                      data-csa-c-coachingID={coachingID}
                      data-csa-c-is-tm={isTm}
                      data-csa-c-requester-role={getRequesterRole(isOm, isTm)}
                      data-cy='send-comment-button'
                      type='primary'
                      size='medium'
                      minWidth={150}
                      onClick={() => onClickAddCommentSendButton()}
                      disabled={
                        addCommentValue === '' ||
                        addCommentValue.length > 500 ||
                        addCoachingCommentState === DataState.LOADING ||
                        !canUserAddComment
                      }
                    >
                      {addCoachingCommentState === DataState.LOADING ? (
                        <Loader size='small' />
                      ) : (
                        <FormattedMessage
                          id='cs_perfportal_send'
                          defaultMessage='Send'
                        />
                      )}
                    </Button>
                  </Row>
                </ModalFooter>
              </>
            )}
          </Box>
        )}
      </Modal>

      <TimeoutToast
        show={addCoachingCommentState === DataState.ERROR}
        message={
          addCoachingComment &&
          addCoachingComment.error &&
          addCoachingComment!.error!.includes(canAccessAgentResourceErrKeyword)
            ? intl.formatMessage({
                id: 'cs_perfportal_coaching_comment_send_no_permission',
                defaultMessage:
                  'Permissions needed to comment on this coaching session',
              })
            : intl.formatMessage({
                id: 'cs_perfportal_coaching_comment_send_error',
                defaultMessage: 'Comment failed to send',
              })
        }
        duration={3000}
        type='error'
        onClose={(): void => {
          setAddCoachingCommentState(DataState.INITIALIZED);
        }}
      />

      <TimeoutToast
        show={
          addCoachingCommentState === DataState.LOADED ||
          addCoachingCommentState === DataState.SUCCESS
        }
        message={getSuccessToastMessage(intl)}
        duration={3000}
        type='success'
        onClose={(): void => {
          setAddCoachingCommentState(DataState.INITIALIZED);
        }}
      />
    </>
  );
};

export default CoachingCommentModal;
